import { Check } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Card } from '@/components/ui'
import { cn } from '@/lib/utils'

import { WIZARD_STEP_ORDER } from '../wizardSteps'
import type { WizardStepId } from '../wizardSteps'

export interface WizardShellProps {
  readonly activeStep: WizardStepId
  readonly children: ReactNode
}

/**
 * Casca visual da wizard: cabecalho + stepper (deriva do estado do backend, nunca clicavel — a
 * navegacao entre passos e sempre consequencia do progresso real, nunca escolha livre do usuario)
 * + o cartao unico onde o passo ativo renderiza. Fonte: `docs/design/prototype/Auth e
 * Onboarding.dc.html` (secao "Onboarding — prototipo navegavel").
 */
export function WizardShell({ activeStep, children }: WizardShellProps) {
  const { t } = useTranslation()
  const activeIndex = WIZARD_STEP_ORDER.indexOf(activeStep)

  return (
    <main className="flex min-h-full flex-col items-center gap-6 bg-background-default p-4 py-10 sm:p-8">
      <div className="flex w-full max-w-3xl flex-col gap-6">
        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-foreground-muted">
            {t('productName')}
          </p>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h1 className="text-xl font-semibold text-foreground-primary sm:text-2xl">
              {t('onboarding.wizard.title')}
            </h1>
            <span className="text-sm text-foreground-muted">
              {t('onboarding.wizard.stepOf', {
                current: activeIndex + 1,
                total: WIZARD_STEP_ORDER.length,
              })}
            </span>
          </div>
        </div>

        <ol className="flex flex-wrap gap-2" aria-label={t('onboarding.wizard.stepperLabel')}>
          {WIZARD_STEP_ORDER.map((stepId, index) => {
            const state =
              index < activeIndex ? 'done' : index === activeIndex ? 'active' : 'pending'

            return (
              <li key={stepId}>
                <span
                  aria-current={state === 'active' ? 'step' : undefined}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold',
                    state === 'active' &&
                      'border-action-primary bg-action-primary text-foreground-on-action',
                    state === 'done' &&
                      'border-status-success/40 bg-status-success-subtle text-status-success',
                    state === 'pending' &&
                      'border-border-default bg-background-elevated text-foreground-muted',
                  )}
                >
                  {state === 'done' ? (
                    <Check aria-hidden="true" className="h-3.5 w-3.5" />
                  ) : (
                    <span aria-hidden="true">{index + 1}</span>
                  )}
                  {t(`onboarding.wizard.steps.${stepId}`)}
                </span>
              </li>
            )
          })}
        </ol>

        <Card className="min-h-[420px] overflow-hidden">{children}</Card>
      </div>
    </main>
  )
}
